if (!customElements.get('ly-header-noticebar-v2')) {
    customElements.define('ly-header-noticebar-v2', class extends HTMLElement {
        #track = null
        #items = []
        #headClone = null
        #tailClone = null
        #index = 0
        #interval = 5000
        #playTimer = null
        #resetTimer = null
        #isHovered = false
        #isDragging = false
        #dragController = null
        #dragBaseX = 0
        #activeTouchId = null
        #touchAxis = null
        #touchStartX = 0
        #touchStartY = 0
        #touchStartTime = 0
        #suppressTouchClick = false
        #touchClickResetTimer = null
        #previousTouchAction = ''
        #previousUserSelect = ''

        connectedCallback() {
            this.#track = this.querySelector('.ly-header-noticebar-v2-list')
            if (!this.#track) return

            // Shopify Section 重载时清除上一次生成的克隆项。
            this.#track.querySelectorAll('[data-noticebar-clone]').forEach(clone => clone.remove())
            this.#items = [...this.#track.querySelectorAll('.ly-header-noticebar-v2-item')]
            this.#interval = Math.max(1000, (Number(this.dataset.interval) || 5) * 1000)
            this.#index = 0

            if (this.#items.length > 1) {
                this.#appendBoundaryClones()
                this.#index = 1
            }

            this.#applyPosition(false)
            this.#updateActiveState()

            if (this.#items.length <= 1) return

            this.#previousTouchAction = this.style.touchAction
            this.#previousUserSelect = this.style.userSelect
            this.style.touchAction = 'pan-y'
            this.style.userSelect = 'none'

            this.#bindEvents()
            this.startPlay()
        }

        disconnectedCallback() {
            this.stopPlay()
            clearTimeout(this.#resetTimer)
            clearTimeout(this.#touchClickResetTimer)
            this.#resetTimer = null
            this.#touchClickResetTimer = null

            this.#removeEvents()
            // unregister 可能在正在拖动时触发 onCancel，需再次清理其恢复的播放定时器。
            this.stopPlay()
            this.#headClone?.remove()
            this.#tailClone?.remove()

            if (this.#track) {
                this.#track.style.removeProperty('transition')
                this.#track.style.removeProperty('transform')
            }

            this.style.touchAction = this.#previousTouchAction
            this.style.userSelect = this.#previousUserSelect

            this.#track = null
            this.#items = []
            this.#headClone = null
            this.#tailClone = null
            this.#index = 0
            this.#isHovered = false
            this.#isDragging = false
            this.#dragBaseX = 0
            this.#resetTouchState()
            this.#suppressTouchClick = false
        }

        #appendBoundaryClones = () => {
            this.#headClone = this.#createClone(this.#items[this.#items.length - 1])
            this.#tailClone = this.#createClone(this.#items[0])
            this.#track.prepend(this.#headClone)
            this.#track.appendChild(this.#tailClone)
        }

        #createClone = (item) => {
            const clone = item.cloneNode(true)
            clone.dataset.noticebarClone = ''
            clone.setAttribute('aria-hidden', 'true')

            if ('inert' in clone) {
                clone.inert = true
            } else {
                clone.querySelectorAll('a, button, input, select, textarea, [tabindex]').forEach(element => {
                    element.setAttribute('tabindex', '-1')
                })
            }

            return clone
        }

        #bindEvents = () => {
            this.#track.addEventListener('transitionend', this.#handleTransitionEnd)
            this.addEventListener('mouseenter', this.#handleMouseEnter)
            this.addEventListener('mouseleave', this.#handleMouseLeave)
            this.addEventListener('focusin', this.#handleFocusIn)
            this.addEventListener('focusout', this.#handleFocusOut)
            this.addEventListener('touchstart', this.#handleTouchStart, { passive: true })
            this.addEventListener('touchmove', this.#handleTouchMove, { passive: false })
            this.addEventListener('touchend', this.#handleTouchEnd, { passive: true })
            this.addEventListener('touchcancel', this.#handleTouchCancel, { passive: true })
            this.addEventListener('click', this.#handleTouchClick, true)
            document.addEventListener('visibilitychange', this.#handleVisibilityChange)

            this.#dragController = createDragController({
                el: this,
                shouldStart: event => event.pointerType === 'mouse',
                onStart: this.#handleDragStart,
                onMove: this.#handleDragMove,
                onEnd: this.#handleDragEnd,
                onCancel: this.#handleDragCancel
            })
            this.#dragController.register()
        }

        #removeEvents = () => {
            this.#dragController?.unregister()
            this.#dragController = null

            this.#track?.removeEventListener('transitionend', this.#handleTransitionEnd)
            this.removeEventListener('mouseenter', this.#handleMouseEnter)
            this.removeEventListener('mouseleave', this.#handleMouseLeave)
            this.removeEventListener('focusin', this.#handleFocusIn)
            this.removeEventListener('focusout', this.#handleFocusOut)
            this.removeEventListener('touchstart', this.#handleTouchStart)
            this.removeEventListener('touchmove', this.#handleTouchMove)
            this.removeEventListener('touchend', this.#handleTouchEnd)
            this.removeEventListener('touchcancel', this.#handleTouchCancel)
            this.removeEventListener('click', this.#handleTouchClick, true)
            document.removeEventListener('visibilitychange', this.#handleVisibilityChange)
        }

        #showNext = () => {
            this.#playTimer = null
            if (!this.#track || this.#items.length <= 1) return

            this.#moveToIndex(this.#index + 1)
            this.startPlay()
        }

        #moveToIndex = (index) => {
            this.#index = Math.max(0, Math.min(this.#items.length + 1, index))
            this.#applyPosition(true)
            this.#updateActiveState()
            this.#scheduleLoopReset()
        }

        #applyPosition = (animate) => {
            if (!this.#track) return

            this.#track.style.transition = animate
                ? 'transform .35s cubic-bezier(.2, 0, 0, 1)'
                : 'none'
            this.#track.style.transform = `translate3d(-${this.#index * 100}%, 0, 0)`
        }

        #scheduleLoopReset = () => {
            clearTimeout(this.#resetTimer)
            this.#resetTimer = null

            if (this.#index !== 0 && this.#index !== this.#items.length + 1) return
            this.#resetTimer = setTimeout(this.#resetLoop, 450)
        }

        #resetLoop = () => {
            clearTimeout(this.#resetTimer)
            this.#resetTimer = null
            if (!this.#track || this.#items.length <= 1) return

            if (this.#index === 0) {
                this.#index = this.#items.length
            } else if (this.#index === this.#items.length + 1) {
                this.#index = 1
            } else {
                return
            }

            this.#applyPosition(false)
            this.#updateActiveState()
        }

        #updateActiveState = () => {
            if (!this.#items.length) return

            const activeIndex = (this.#index - 1 + this.#items.length) % this.#items.length
            this.#items.forEach((item, index) => {
                item.classList.toggle('is-active', index === activeIndex)
            })
        }

        #handleTransitionEnd = (event) => {
            if (event.target !== this.#track || event.propertyName !== 'transform') return

            this.#resetLoop()
        }

        #handleDragStart = () => {
            this.#isDragging = true
            this.stopPlay()
            this.#resetLoop()
            clearTimeout(this.#resetTimer)
            this.#resetTimer = null

            const trackRect = this.#track.getBoundingClientRect()
            const containerRect = this.getBoundingClientRect()
            this.#dragBaseX = trackRect.left - containerRect.left
            this.#track.style.transition = 'none'
            this.#track.style.transform = `translate3d(${this.#dragBaseX}px, 0, 0)`
        }

        #handleDragMove = ({ dx }) => {
            if (!this.#track) return

            this.#track.style.transform = `translate3d(${this.#dragBaseX + dx}px, 0, 0)`
        }

        #handleDragEnd = ({ dx, velocity }) => {
            this.#settleDrag({ dx, velocity })
            this.#isDragging = false
            this.startPlay()
        }

        #handleDragCancel = () => {
            this.#applyPosition(true)
            this.#isDragging = false
            this.startPlay()
        }

        #handleTouchStart = (event) => {
            if (event.touches.length !== 1) {
                this.#cancelTouchGesture()
                return
            }

            const touch = event.touches[0]
            this.#activeTouchId = touch.identifier
            this.#touchAxis = null
            this.#touchStartX = touch.clientX
            this.#touchStartY = touch.clientY
            this.#touchStartTime = performance.now()
        }

        #handleTouchMove = (event) => {
            if (this.#activeTouchId === null) return
            if (event.touches.length !== 1) {
                this.#cancelTouchGesture()
                return
            }

            const touch = this.#findTouch(event.touches, this.#activeTouchId)
            if (!touch) {
                this.#cancelTouchGesture()
                return
            }

            const dx = touch.clientX - this.#touchStartX
            const dy = touch.clientY - this.#touchStartY

            if (this.#touchAxis === null) {
                if (Math.max(Math.abs(dx), Math.abs(dy)) < 5) return

                this.#touchAxis = Math.abs(dx) > Math.abs(dy) ? 'horizontal' : 'vertical'
                if (this.#touchAxis === 'vertical') return

                this.#suppressTouchClick = true
                this.#handleDragStart()
            }

            if (this.#touchAxis !== 'horizontal') return

            event.preventDefault()
            this.#handleDragMove({ dx })
        }

        #handleTouchEnd = (event) => {
            if (this.#activeTouchId === null) return

            const touch = this.#findTouch(event.changedTouches, this.#activeTouchId)
            if (!touch) return

            if (this.#touchAxis === 'horizontal') {
                const dx = touch.clientX - this.#touchStartX
                const elapsed = Math.max(1, performance.now() - this.#touchStartTime)
                this.#handleDragEnd({
                    dx,
                    velocity: dx / elapsed
                })
                this.#scheduleTouchClickReset()
            }

            this.#resetTouchState()
        }

        #handleTouchCancel = (event) => {
            if (this.#activeTouchId === null) return
            if (event.changedTouches.length && !this.#findTouch(event.changedTouches, this.#activeTouchId)) return

            if (this.#touchAxis === 'horizontal') {
                this.#handleDragCancel()
                this.#scheduleTouchClickReset()
            }

            this.#resetTouchState()
        }

        #cancelTouchGesture = () => {
            if (this.#touchAxis === 'horizontal') {
                this.#handleDragCancel()
                this.#scheduleTouchClickReset()
            }

            this.#resetTouchState()
        }

        #resetTouchState = () => {
            this.#activeTouchId = null
            this.#touchAxis = null
            this.#touchStartX = 0
            this.#touchStartY = 0
            this.#touchStartTime = 0
        }

        #findTouch = (touches, identifier) => {
            for (let index = 0; index < touches.length; index += 1) {
                if (touches[index].identifier === identifier) return touches[index]
            }

            return null
        }

        #scheduleTouchClickReset = () => {
            clearTimeout(this.#touchClickResetTimer)
            this.#touchClickResetTimer = setTimeout(() => {
                this.#suppressTouchClick = false
                this.#touchClickResetTimer = null
            }, 150)
        }

        #handleTouchClick = (event) => {
            if (!this.#suppressTouchClick) return

            event.preventDefault()
            event.stopImmediatePropagation()
            this.#suppressTouchClick = false
        }

        #settleDrag = ({ dx, velocity }) => {
            const width = this.clientWidth || 1
            const threshold = Math.min(width * .18, 72)
            const isFarEnough = Math.abs(dx) >= threshold
            const isFastEnough = Math.abs(dx) >= 12 && Math.abs(velocity) >= .5

            if (isFarEnough || isFastEnough) {
                this.#moveToIndex(this.#index + (dx < 0 ? 1 : -1))
                return
            }

            this.#applyPosition(true)
        }

        #handleMouseEnter = () => {
            this.#isHovered = true
            this.stopPlay()
        }

        #handleMouseLeave = () => {
            this.#isHovered = false
            this.startPlay()
        }

        #handleFocusIn = () => this.stopPlay()

        #handleFocusOut = () => {
            requestAnimationFrame(() => this.startPlay())
        }

        #handleVisibilityChange = () => {
            if (document.hidden) {
                this.stopPlay()
                return
            }

            this.startPlay()
        }

        startPlay = () => {
            if (!this.#track || this.#items.length <= 1 || this.#playTimer || document.hidden) return
            if (this.#isHovered || this.#isDragging || this.contains(document.activeElement)) return

            this.#playTimer = setTimeout(this.#showNext, this.#interval)
        }

        stopPlay = () => {
            clearTimeout(this.#playTimer)
            this.#playTimer = null
        }
    })
}
